import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  noteImagePath,
  parseNoteImage,
  type NoteImageMetadata,
} from "../note-image.ts";

/** Show saved evidence for the selected note using its existing private HTTP read. */
export function NoteImage({
  siteId,
  noteId,
  screenshot,
}: {
  readonly siteId: string;
  readonly noteId: string;
  readonly screenshot: unknown;
}) {
  const image = parseNoteImage(screenshot);
  if (image.kind === "absent") return null;
  if (image.kind === "invalid")
    return (
      <p className="mt-5 text-sm text-muted-foreground">
        Image details are unavailable. Refresh the notes to try again.
      </p>
    );
  return (
    <StoredImage
      key={siteId + ":" + noteId + ":" + image.id}
      path={noteImagePath(siteId, noteId)}
      image={image}
    />
  );
}

function StoredImage({
  path,
  image,
}: {
  readonly path: string;
  readonly image: Extract<NoteImageMetadata, { kind: "ready" }>;
}) {
  const [attempt, setAttempt] = useState(0);
  return (
    <ImageAttempt
      key={attempt}
      path={path + "?attempt=" + attempt}
      image={image}
      retry={() => setAttempt((current) => current + 1)}
    />
  );
}

function ImageAttempt({
  path,
  image,
  retry,
}: {
  readonly path: string;
  readonly image: Extract<NoteImageMetadata, { kind: "ready" }>;
  readonly retry: () => void;
}) {
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");
  const description = image.reconstructed
    ? "Saved DOM reconstruction of the website when this comment was created."
    : "Image attached when this comment was created.";
  return (
    <figure className="note-image mt-5">
      <Dialog>
        <div className="relative flex h-32 items-center justify-center overflow-hidden border bg-muted">
          {state !== "failed" && (
            <img
              src={path}
              alt="Attached visual context"
              width={image.width}
              height={image.height}
              className={
                state === "ready"
                  ? "h-full w-full object-contain"
                  : "invisible h-full w-full object-contain"
              }
              decoding="async"
              referrerPolicy="no-referrer"
              onLoad={() => setState("ready")}
              onError={() => setState("failed")}
            />
          )}
          {state === "ready" && (
            <DialogTrigger
              render={
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="absolute right-2 bottom-2"
                />
              }
            >
              Inspect attached image
            </DialogTrigger>
          )}
          <div
            className="absolute inset-0 flex items-center justify-center gap-3 p-3 text-sm text-muted-foreground pointer-events-none"
            role="status"
          >
            {state === "loading"
              ? "Loading image…"
              : state === "failed"
                ? "Image could not be loaded."
                : ""}
            {state === "failed" && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="pointer-events-auto"
                onClick={retry}
              >
                Retry image
              </Button>
            )}
          </div>
        </div>
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Attached image</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <ExpandedImage
            path={path}
            width={image.width}
            height={image.height}
          />
        </DialogContent>
      </Dialog>
      <figcaption className="mt-2 text-xs text-muted-foreground">
        {image.reconstructed ? "DOM reconstruction" : "Attached image"} ·{" "}
        {image.width} × {image.height}
      </figcaption>
    </figure>
  );
}

function ExpandedImage({
  path,
  width,
  height,
}: {
  readonly path: string;
  readonly width: number;
  readonly height: number;
}) {
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  return (
    <div className="flex min-h-32 items-center justify-center bg-muted">
      {failed ? (
        <div className="flex flex-wrap items-center gap-3 p-4">
          <p role="status">Image could not be loaded.</p>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setAttempt((value) => value + 1);
              setFailed(false);
            }}
          >
            Retry image
          </Button>
        </div>
      ) : (
        <img
          key={attempt}
          src={path + "&view=" + attempt}
          alt="Saved visual context for this comment"
          width={width}
          height={height}
          className="max-h-[65dvh] max-w-full object-contain"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}
